import { useState } from 'react';
import type { GameState, HoleInfo, HoleScore, Player, Team } from '@/lib/golfEngine';
import { formatStatus, playerReceivesStrokeOnHole, getStrokesOnHole, getCompletedHolesInOrder, getFrontHoles, getBackHoles } from '@/lib/golfEngine';
import { getGameEngine } from '@/games/index';
import { cn } from '@/lib/utils';
import { Info } from 'lucide-react';
import { getPlayingOrder } from '@/lib/golfEngine';
import { DT, dtDollarColor, dtScoreColor, dtStatusColor } from '@/lib/darkTheme';

function InfoPopup() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center"
        data-testid="dollar-status-info"
      >
        <Info className="w-3.5 h-3.5" style={{ color: DT.primaryText, opacity: 0.5 }} />
      </button>
      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative rounded-xl shadow-xl mx-4 max-w-sm w-full p-4"
            style={{ background: DT.surface, border: `1px solid ${DT.divider}` }}
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-2 right-2 text-lg leading-none"
              style={{ color: DT.primaryText, opacity: 0.5 }}
              data-testid="info-popup-close"
            >
              &times;
            </button>
            <p style={{ color: DT.primaryText, fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>How payouts are calculated</p>
            <p style={{ color: DT.primaryText, opacity: 0.7, fontSize: '14px', lineHeight: 1.6 }}>
              These amounts show how much you're up or down on the Front, Back, and Overall right now, based on completed holes and presses. If both sides play even from this point to the end of each segment, these will be the final results.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

// Compute current money status for display
function computeMoneyStatus(gameState: GameState): {
  teamAhead: 'team1' | 'team2' | 'tied';
  teamName: string;
  frontAmount: number;
  backAmount: number;
  overallAmount: number;
  totalAmount: number;
} {
  const completedHoles = getCompletedHolesInOrder(gameState);
  
  // If no holes completed, return all zeros
  if (completedHoles.length === 0) {
    return {
      teamAhead: 'tied',
      teamName: '',
      frontAmount: 0,
      backAmount: 0,
      overallAmount: 0,
      totalAmount: 0,
    };
  }
  
  // Use the settlement system to get dollar amounts
  const engine = getGameEngine(gameState.gameType);
  const settlement = engine.settleGame(gameState);
  
  // Team 1 = A, Team 2 = B
  // totalAmountForA > 0 means Team 1 is ahead
  // totalAmountForA < 0 means Team 2 is ahead
  const team1Name = formatTeamDisplayName(gameState.teams[0]);
  const team2Name = formatTeamDisplayName(gameState.teams[1]);
  
  if (settlement.totalAmountForA > 0) {
    return {
      teamAhead: 'team1',
      teamName: team1Name,
      frontAmount: settlement.perLeg.Front.amountForA,
      backAmount: settlement.perLeg.Back.amountForA,
      overallAmount: settlement.perLeg.Overall.amountForA,
      totalAmount: settlement.totalAmountForA,
    };
  } else if (settlement.totalAmountForA < 0) {
    // Team 2 is ahead, show positive amounts from their perspective
    return {
      teamAhead: 'team2',
      teamName: team2Name,
      frontAmount: -settlement.perLeg.Front.amountForA,
      backAmount: -settlement.perLeg.Back.amountForA,
      overallAmount: -settlement.perLeg.Overall.amountForA,
      totalAmount: -settlement.totalAmountForA,
    };
  } else {
    return {
      teamAhead: 'tied',
      teamName: '',
      frontAmount: 0,
      backAmount: 0,
      overallAmount: 0,
      totalAmount: 0,
    };
  }
}

// Format dollar amount for display
function formatDollar(amount: number): string {
  if (amount === 0) return '$0';
  if (amount > 0) return `$${amount}`;
  return `($${Math.abs(amount)})`;
}

// Money Status Bar Component
function MoneyStatusBar({ gameState }: { gameState: GameState }) {
  const status = computeMoneyStatus(gameState);
  const completedHoles = getCompletedHolesInOrder(gameState);
  
  if (completedHoles.length === 0) {
    return (
      <div style={{ background: DT.surface, borderBottom: `1px solid ${DT.divider}` }} className="px-4 py-2 text-center">
        <span style={{ color: DT.primaryText, opacity: 0.5, fontWeight: 600, fontSize: '16px' }}>No holes completed yet</span>
      </div>
    );
  }
  
  if (status.teamAhead === 'tied') {
    return (
      <div style={{ background: DT.surface, borderBottom: `1px solid ${DT.divider}` }} className="px-4 py-2 text-center">
        <span style={{ color: DT.primaryText, fontWeight: 800, fontSize: '16px' }}>Match is EVEN</span>
      </div>
    );
  }
  
  // Show the team that's ahead with their amounts by leg
  const team1Name = formatTeamDisplayName(gameState.teams[0]);
  const isTeam1Ahead = status.teamAhead === 'team1';
  
  const losingTeamName = isTeam1Ahead 
    ? formatTeamDisplayName(gameState.teams[1])
    : team1Name;
  
  return (
    <div style={{
      background: isTeam1Ahead ? DT.team1Bg : DT.team2Bg,
      borderBottom: `1px solid ${DT.divider}`,
    }} className="px-4 py-2 text-center">
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <span style={{ color: DT.primaryText, fontWeight: 800, fontSize: '17px' }}>
          {status.teamName}
        </span>
        <span style={{ color: DT.primaryText, opacity: 0.4 }}>–</span>
        <span style={{ color: DT.primaryText, fontWeight: 900, fontSize: '18px' }}>
          Front <span style={{ fontFamily: 'monospace', color: DT.winning, fontWeight: 900, fontSize: '18px' }}>{formatDollar(status.frontAmount)}</span>
        </span>
        <span style={{ color: DT.primaryText, opacity: 0.4, fontWeight: 900, fontSize: '18px' }}>|</span>
        <span style={{ color: DT.primaryText, fontWeight: 900, fontSize: '18px' }}>
          Back <span style={{ fontFamily: 'monospace', color: DT.winning, fontWeight: 900, fontSize: '18px' }}>{formatDollar(status.backAmount)}</span>
        </span>
        <span style={{ color: DT.primaryText, opacity: 0.4, fontWeight: 900, fontSize: '18px' }}>|</span>
        <span style={{ color: DT.primaryText, fontWeight: 900, fontSize: '18px' }}>
          Overall <span style={{ fontFamily: 'monospace', color: DT.winning, fontWeight: 900, fontSize: '18px' }}>{formatDollar(status.overallAmount)}</span>
        </span>
        <span style={{ color: DT.primaryText, opacity: 0.4, fontWeight: 900, fontSize: '18px' }}>|</span>
        <span style={{ color: DT.winning, fontWeight: 900, fontSize: '18px' }}>
          Total: {formatDollar(status.totalAmount)}
        </span>
        <InfoPopup />
      </div>
      <div style={{ color: DT.losing, fontWeight: 700, fontSize: '14px', marginTop: '2px' }}>
        {losingTeamName} owes {formatDollar(status.totalAmount)}
      </div>
    </div>
  );
}

interface ScorecardShellProps {
  gameState: GameState;
  onHoleSelect?: (holeNumber: number) => void;
  onHoleHeaderClick?: (holeNumber: number) => void;
  correctionMode?: boolean;
  correctionFromHole?: number | null;
  onCorrectionHoleSelect?: (holeNumber: number) => void;
}

interface ScoreCellProps {
  score: HoleScore | undefined;
  player: Player;
  hole: HoleInfo;
  onHoleHeaderClick?: (holeNumber: number) => void;
  isTeam1: boolean;
}

function ScoreCell({ score, player, hole, onHoleHeaderClick, isTeam1 }: ScoreCellProps) {
  const receivesStroke = playerReceivesStrokeOnHole(player, hole);
  const strokeCount = getStrokesOnHole(player, hole);
  const hasScore = score?.grossScore !== null && score?.grossScore !== undefined;

  const scoreColor = hasScore ? dtScoreColor(score!.grossScore!, hole.par) : DT.primaryText;

  return (
    <div
      className="relative h-8 flex items-center justify-center cursor-pointer transition-colors duration-150"
      style={{
        borderRight: `1px solid ${DT.divider}`,
        background: isTeam1 ? DT.team1Bg : DT.team2Bg,
      }}
      onClick={() => onHoleHeaderClick?.(hole.number)}
      data-testid={`score-cell-${player.id}-${hole.number}`}
    >
      {hasScore ? (
        <span style={{ fontFamily: 'monospace', fontSize: '15px', fontWeight: 900, color: scoreColor }}>
          {receivesStroke ? `${score!.grossScore}/${score!.netScore}` : score!.grossScore}
        </span>
      ) : strokeCount > 0 ? (
        <span className="absolute top-0.5 right-0.5 flex items-center gap-[1px]">
          {Array.from({ length: strokeCount }, (_, i) => (
            <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: DT.gold }} />
          ))}
        </span>
      ) : (
        <span style={{ color: DT.primaryText, opacity: 0.2, fontSize: '10px' }}>-</span>
      )}
    </div>
  );
}

function formatScorecardName(name: string, maxLen: number = 8): string {
  if (!name) return '-';
  if (name.length <= maxLen) return name;
  return name.substring(0, maxLen);
}

function formatTeamDisplayName(team: Team): string {
  if (team.players.length === 1) {
    return formatScorecardName(team.players[0]?.name ?? 'Player', 8);
  }
  const p1 = formatScorecardName(team.players[0]?.name || '', 4);
  const p2 = formatScorecardName(team.players[1]?.name || '', 4);
  return `${p1}/${p2}`;
}

function getPlayerRows(state: GameState): Array<{ player: Player; team: Team; isTeam1: boolean; rowIndex: number }> {
  const rows: Array<{ player: Player; team: Team; isTeam1: boolean; rowIndex: number }> = [];

  if (state.matchFormat === '2v2') {
    if (state.teams[0]?.players[0]) {
      rows.push({ player: state.teams[0].players[0], team: state.teams[0], isTeam1: true, rowIndex: 0 });
    }
    if (state.teams[0]?.players[1]) {
      rows.push({ player: state.teams[0].players[1], team: state.teams[0], isTeam1: true, rowIndex: 1 });
    }
    if (state.teams[1]?.players[0]) {
      rows.push({ player: state.teams[1].players[0], team: state.teams[1], isTeam1: false, rowIndex: 2 });
    }
    if (state.teams[1]?.players[1]) {
      rows.push({ player: state.teams[1].players[1], team: state.teams[1], isTeam1: false, rowIndex: 3 });
    }
  } else {
    if (state.teams[0]?.players[0]) {
      rows.push({ player: state.teams[0].players[0], team: state.teams[0], isTeam1: true, rowIndex: 0 });
    }
    rows.push({ player: { id: 'empty-1', name: '', handicap: 0, strokesReceived: 0, courseHandicap: 0, teeBoxId: '' }, team: state.teams[0], isTeam1: true, rowIndex: 1 });
    if (state.teams[1]?.players[0]) {
      rows.push({ player: state.teams[1].players[0], team: state.teams[1], isTeam1: false, rowIndex: 2 });
    }
    rows.push({ player: { id: 'empty-2', name: '', handicap: 0, strokesReceived: 0, courseHandicap: 0, teeBoxId: '' }, team: state.teams[1], isTeam1: false, rowIndex: 3 });
  }

  return rows;
}

export function ScorecardShell({ gameState, onHoleSelect, onHoleHeaderClick, correctionMode, correctionFromHole, onCorrectionHoleSelect }: ScorecardShellProps) {
  const frontHoleNums = getFrontHoles(gameState.startingHole);
  const backHoleNums = getBackHoles(gameState.startingHole);
  const firstSection = gameState.holes.filter(h => frontHoleNums.includes(h.number));
  const secondSection = gameState.holes.filter(h => backHoleNums.includes(h.number));
  const firstLabel = gameState.startingHole === 1 ? 'FRONT' : 'BACK';
  const secondLabel = gameState.startingHole === 1 ? 'BACK' : 'FRONT';
  const firstTotal = gameState.startingHole === 1 ? 'OUT' : 'IN';
  const secondTotal = gameState.startingHole === 1 ? 'IN' : 'OUT';
  const playerRows = getPlayerRows(gameState);

  const gridCols = "grid-cols-[80px_repeat(9,44px)_40px]";

  const playingOrder = getPlayingOrder(gameState.startingHole);
  const completedHoles = new Set(
    getCompletedHolesInOrder(gameState)
  );
  const wouldBeCleared = (holeNum: number): boolean => {
    if (!correctionMode || correctionFromHole === null || correctionFromHole === undefined) return false;
    const fromIdx = playingOrder.indexOf(correctionFromHole);
    const thisIdx = playingOrder.indexOf(holeNum);
    return thisIdx >= fromIdx && completedHoles.has(holeNum);
  };

  const renderHoleSection = (holes: HoleInfo[], label: string, totalLabel: string) => (
    <div className="flex flex-col">
      <div className={cn("grid", gridCols)} style={{ borderBottom: `2px solid ${DT.divider}` }}>
        <div className="flex items-center justify-center py-1" style={{ fontWeight: 800, fontSize: '12px', color: DT.primaryText, background: DT.brandEmerald, borderRight: `1px solid ${DT.divider}`, position: 'sticky', left: 0, zIndex: 10 }}>
          {label}
        </div>
        {holes.map((hole) => {
          const isCleared = wouldBeCleared(hole.number);
          const isCorrectionTarget = correctionMode && completedHoles.has(hole.number);
          return (
            <div
              key={hole.number}
              className="flex items-center justify-center py-1 cursor-pointer transition-colors"
              style={{
                fontWeight: 800, fontSize: '13px',
                color: isCleared ? DT.losing : isCorrectionTarget ? DT.gold : DT.gold,
                background: isCleared ? DT.loseBg : isCorrectionTarget ? 'rgba(255,215,64,0.15)' : DT.brandEmerald,
                borderRight: `1px solid ${DT.divider}`,
              }}
              onClick={() => {
                if (correctionMode && onCorrectionHoleSelect) {
                  onCorrectionHoleSelect(hole.number);
                } else {
                  onHoleHeaderClick?.(hole.number);
                }
              }}
              data-testid={`hole-header-${hole.number}`}
            >
              {hole.number}
            </div>
          );
        })}
        <div className="flex items-center justify-center py-1" style={{ fontWeight: 800, fontSize: '12px', color: DT.primaryText, background: DT.brandEmerald }}>
          {totalLabel}
        </div>
      </div>

      <div className={cn("grid", gridCols)} style={{ borderBottom: `1px solid ${DT.divider}` }}>
        <div className="flex items-center justify-center py-0.5" style={{ fontSize: '11px', fontWeight: 700, color: DT.primaryText, opacity: 0.7, background: DT.darkBg, borderRight: `1px solid ${DT.divider}`, position: 'sticky', left: 0, zIndex: 10 }}>
          Par
        </div>
        {holes.map((hole) => (
          <div
            key={hole.number}
            className="flex items-center justify-center py-0.5"
            style={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 700, color: DT.primaryText, opacity: 0.7, background: DT.darkBg, borderRight: `1px solid ${DT.divider}` }}
          >
            {hole.par}
          </div>
        ))}
        <div className="flex items-center justify-center py-0.5" style={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 700, color: DT.primaryText, opacity: 0.7, background: DT.darkBg }}>
          {holes.reduce((sum, h) => sum + h.par, 0)}
        </div>
      </div>

      <div className={cn("grid", gridCols)} style={{ borderBottom: `1px solid ${DT.divider}` }}>
        <div className="flex items-center justify-center py-0.5" style={{ fontSize: '11px', fontWeight: 700, color: DT.primaryText, opacity: 0.65, background: DT.darkBg, borderRight: `1px solid ${DT.divider}`, position: 'sticky', left: 0, zIndex: 10 }}>
          Yds
        </div>
        {holes.map((hole) => (
          <div
            key={hole.number}
            className="flex items-center justify-center py-0.5"
            style={{ fontSize: '11px', fontFamily: 'monospace', color: DT.primaryText, opacity: 0.65, background: DT.darkBg, borderRight: `1px solid ${DT.divider}` }}
          >
            {hole.yardage}
          </div>
        ))}
        <div className="flex items-center justify-center py-0.5" style={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 700, color: DT.primaryText, opacity: 0.65, background: DT.darkBg }}>
          {holes.reduce((sum, h) => sum + h.yardage, 0)}
        </div>
      </div>

      <div className={cn("grid", gridCols)} style={{ borderBottom: `1px solid ${DT.divider}` }}>
        <div className="flex items-center justify-center py-0.5" style={{ fontSize: '11px', fontWeight: 700, color: DT.primaryText, opacity: 0.65, background: DT.darkBg, borderRight: `1px solid ${DT.divider}`, position: 'sticky', left: 0, zIndex: 10 }}>
          Hcp
        </div>
        {holes.map((hole) => (
          <div
            key={hole.number}
            className="flex items-center justify-center py-0.5"
            style={{ fontSize: '11px', fontFamily: 'monospace', color: DT.primaryText, opacity: 0.65, background: DT.darkBg, borderRight: `1px solid ${DT.divider}` }}
          >
            {hole.strokeIndex}
          </div>
        ))}
        <div style={{ background: DT.darkBg }} />
      </div>

      {playerRows.map((row) => {
        const isEmpty = row.player.id.startsWith('empty-');
        const teamBg = row.isTeam1 ? DT.team1Bg : DT.team2Bg;
        
        return (
          <div key={row.player.id} className={cn("grid", gridCols)} style={{ borderBottom: `1px solid ${DT.divider}`, opacity: isEmpty ? 0.3 : 1 }}>
            <div
              className="relative flex items-center px-1.5 h-8 overflow-hidden"
              style={{ background: row.isTeam1 ? DT.team1StickyBg : DT.team2StickyBg, borderRight: `1px solid ${DT.divider}`, position: 'sticky', left: 0, zIndex: 10 }}
            >
              <span className="truncate leading-tight" style={{ color: DT.primaryText, fontWeight: 800, fontSize: '15px' }}>{formatScorecardName(row.player.name)}</span>
              {row.player.strokesReceived > 0 && (
                <span className="absolute top-0 right-0.5" style={{ fontSize: '9px', fontWeight: 700, color: DT.gold, lineHeight: 1.2 }}>{row.player.strokesReceived}</span>
              )}
            </div>
            {holes.map((hole) => {
              if (isEmpty) {
                return (
                  <div
                    key={hole.number}
                    className="flex items-center justify-center h-8"
                    style={{ borderRight: `1px solid ${DT.divider}`, background: DT.darkBg }}
                  >
                    <span style={{ color: DT.primaryText, opacity: 0.2, fontSize: '10px' }}>-</span>
                  </div>
                );
              }
              
              const score = gameState.scores.find(
                s => s.playerId === row.player.id && s.holeNumber === hole.number
              );
              const cellClickHandler = correctionMode && onCorrectionHoleSelect
                ? onCorrectionHoleSelect
                : onHoleHeaderClick;
              return (
                <ScoreCell
                  key={hole.number}
                  score={score}
                  player={row.player}
                  hole={hole}
                  onHoleHeaderClick={cellClickHandler}
                  isTeam1={row.isTeam1}
                />
              );
            })}
            <div
              className="flex items-center justify-center h-8"
              style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: '13px', color: DT.primaryText, background: teamBg }}
            >
              {isEmpty ? '-' : calculatePlayerTotal(gameState, row.player.id, holes)}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="rounded-lg shadow-xl" style={{ background: DT.surface, border: `1px solid ${DT.divider}`, minWidth: 'max-content' }}>
      <MoneyStatusBar gameState={gameState} />
      
      <div>
        {renderHoleSection(firstSection, firstLabel, firstTotal)}
        
        <div className="h-1" style={{ background: DT.divider }} />
        
        {renderHoleSection(secondSection, secondLabel, secondTotal)}
      </div>

      <div className="px-4 py-1.5" style={{ borderTop: `2px solid ${DT.divider}`, background: DT.darkBg }}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <span style={{ color: DT.primaryText, fontWeight: 900, fontSize: '16px' }}>Front:</span>
              <span style={{
                fontFamily: 'monospace',
                fontWeight: 900,
                fontSize: '20px',
                color: dtStatusColor(gameState.nassauStatus.front),
              }}>
                {formatStatus(gameState.nassauStatus.front)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span style={{ color: DT.primaryText, fontWeight: 900, fontSize: '16px' }}>Back:</span>
              <span style={{
                fontFamily: 'monospace',
                fontWeight: 900,
                fontSize: '20px',
                color: dtStatusColor(gameState.nassauStatus.back),
              }}>
                {formatStatus(gameState.nassauStatus.back)}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span style={{ color: DT.primaryText, fontWeight: 900, fontSize: '16px' }}>Overall:</span>
              <span style={{
                fontFamily: 'monospace',
                fontWeight: 900,
                fontSize: '20px',
                color: dtStatusColor(gameState.nassauStatus.overall),
              }}>
                {formatStatus(gameState.nassauStatus.overall)}
              </span>
            </div>
            <span style={{ color: DT.gold, fontWeight: 900, fontSize: '14px' }}>Original Bet</span>
          </div>
          <div className="flex items-center gap-1" style={{ fontSize: '12px', color: DT.primaryText, opacity: 0.5 }}>
            <span className="w-2 h-2 rounded-full inline-block" style={{ background: DT.gold }} />
            <span>= stroke</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function calculatePlayerTotal(state: GameState, playerId: string, holes: HoleInfo[]): string {
  const scores = state.scores.filter(
    s => s.playerId === playerId && holes.some(h => h.number === s.holeNumber) && s.grossScore !== null
  );
  
  if (scores.length === 0) return '-';
  
  const grossTotal = scores.reduce((sum, s) => sum + (s.grossScore ?? 0), 0);
  const netTotal = scores.reduce((sum, s) => sum + (s.netScore ?? 0), 0);
  
  // Only show gross/net if they differ (player received strokes)
  if (grossTotal !== netTotal) {
    return `${grossTotal}/${netTotal}`;
  }
  return String(grossTotal);
}

export default ScorecardShell;
